import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useTheme } from "../lib/theme";
import { VoteBanner } from "./VoteBanner";
import { api } from "../lib/apiClient";
import { Scale, LogOut, User, LayoutDashboard, Sun, Moon, FileText, ShieldCheck, IdCard, Briefcase, Menu, X } from "lucide-react";

export function Header() {
  const { user, logout } = useAuth();
  const { mode, toggle } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isPro = mode === "pro";
  const isPreinstruction = location.pathname === "/preinstruction";
  const [mobileOpen, setMobileOpen] = useState(false);
  const closeMobile = () => setMobileOpen(false);

  return (
    <>
      <VoteBanner />
      <header className="glass-header sticky top-0 z-40" data-testid="app-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-2 flex-shrink-0" data-testid="logo-link">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${isPro ? "bg-[#FFD35A]" : "bg-[#1E293B]"}`}>
              <Scale className={`w-5 h-5 ${isPro ? "text-[#0F172A]" : "text-[#10B981]"}`} strokeWidth={2.5} />
            </div>
            <div>
              <div className="font-heading font-black text-lg leading-none">JurisFlash</div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-6 text-sm" data-testid="nav-unified">
            <Link
              to="/"
              className={`font-semibold hover:opacity-70 pb-1 border-b-2 transition-colors ${!isPreinstruction ? "text-[#1E293B] border-[#1E293B]" : "text-slate-500 border-transparent"}`}
              aria-current={!isPreinstruction ? "page" : undefined}
              data-testid="nav-espace-citoyen"
            >
              Espace Citoyen
            </Link>
            <Link
              to="/preinstruction"
              className={`font-semibold hover:opacity-70 pb-1 border-b-2 transition-colors ${isPreinstruction ? "text-[#10B981] border-[#10B981]" : "text-slate-500 border-transparent"}`}
              aria-current={isPreinstruction ? "page" : undefined}
              data-testid="nav-espace-pro"
            >
              Espace Professionnel
            </Link>
          </nav>

          <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
            <button onClick={toggle} className="theme-toggle" data-testid="theme-toggle-btn" aria-label="Basculer citoyen/pro">
              <span className={`toggle-face ${mode === "citizen" ? "active" : ""}`}>⚪</span>
              <span className={`toggle-face ${mode === "pro" ? "active" : ""}`}>⚫</span>
            </button>

            {user ? (
              <>
                {isPreinstruction && (
                  <div className="hidden md:flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-xs text-emerald-800" data-testid="pro-user-chip" title="Gestionnaire connecté">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" aria-hidden="true"></span>
                    <span className="font-semibold">Gestionnaire · {user.name?.split(" ")[0] || "Pro"}</span>
                  </div>
                )}
                <button onClick={() => navigate(isPro ? "/dashboard/pro" : "/dashboard/citoyen")} className="hidden md:inline-flex btn-ghost items-center gap-2" data-testid="header-dashboard-btn">
                  <LayoutDashboard className="w-4 h-4" /> {user.name?.split(" ")[0] || "Compte"}
                </button>
                <button onClick={logout} className="btn-ghost w-10 h-10 !p-0 hidden md:flex items-center justify-center" data-testid="logout-btn" aria-label="Déconnexion">
                  <LogOut className="w-4 h-4" />
                </button>
              </>
            ) : (
              <Link to="/login" className="btn-navy !px-4 !py-2 text-sm min-h-[44px] items-center" data-testid="header-login-btn">
                <User className="w-4 h-4" /> <span className="hidden sm:inline">Connexion</span>
              </Link>
            )}

            {/* Bouton hamburger — visible <lg */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="lg:hidden w-11 h-11 flex items-center justify-center rounded-lg hover:bg-slate-100 transition-colors"
              aria-label={mobileOpen ? "Fermer le menu" : "Ouvrir le menu"}
              aria-expanded={mobileOpen}
              data-testid="mobile-menu-btn"
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Menu mobile déroulant */}
        {mobileOpen && (
          <div className="lg:hidden border-t border-slate-200 bg-white shadow-lg" data-testid="mobile-menu-panel">
            <nav className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex flex-col divide-y divide-slate-100">
              <Link
                to="/"
                onClick={closeMobile}
                className={`py-3 px-2 font-semibold flex items-center justify-between rounded-lg ${!isPreinstruction ? "text-[#1E293B] bg-slate-50" : "text-slate-600"}`}
                data-testid="mobile-nav-espace-citoyen"
              >
                <span>Espace Citoyen</span>
                {!isPreinstruction && <span className="text-[0.6rem] uppercase tracking-widest font-bold text-slate-500">Actif</span>}
              </Link>
              <Link
                to="/preinstruction"
                onClick={closeMobile}
                className={`py-3 px-2 font-semibold flex items-center justify-between rounded-lg ${isPreinstruction ? "text-[#10B981] bg-emerald-50" : "text-slate-600"}`}
                data-testid="mobile-nav-espace-pro"
              >
                <span>Espace Professionnel</span>
                {isPreinstruction && <span className="text-[0.6rem] uppercase tracking-widest font-bold text-emerald-700">Actif</span>}
              </Link>
              {user && (
                <>
                  <button
                    onClick={() => { closeMobile(); navigate(isPro ? "/dashboard/pro" : "/dashboard/citoyen"); }}
                    className="py-3 px-2 font-semibold text-left text-slate-700 flex items-center gap-2 rounded-lg"
                    data-testid="mobile-nav-dashboard"
                  >
                    <LayoutDashboard className="w-4 h-4" /> Mon tableau de bord
                  </button>
                  <button
                    onClick={() => { closeMobile(); logout(); }}
                    className="py-3 px-2 font-semibold text-left text-red-600 flex items-center gap-2 rounded-lg"
                    data-testid="mobile-nav-logout"
                  >
                    <LogOut className="w-4 h-4" /> Déconnexion
                  </button>
                </>
              )}
              {!user && (
                <Link
                  to="/login"
                  onClick={closeMobile}
                  className="py-3 px-2 font-semibold text-left text-[#10B981] flex items-center gap-2 rounded-lg"
                  data-testid="mobile-nav-login"
                >
                  <User className="w-4 h-4" /> Connexion
                </Link>
              )}
            </nav>
          </div>
        )}
      </header>
    </>
  );
}

export function Footer() {
  const location = useLocation();
  const isPreinstruction = location.pathname === "/preinstruction";

  if (isPreinstruction) {
    return (
      <footer className="mt-24 border-t border-slate-200 bg-white dark:bg-transparent" data-testid="app-footer-pro">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 grid md:grid-cols-3 gap-8">
          <div>
            <div className="font-heading font-black text-lg">JurisFlash · Espace Professionnel</div>
            <p className="text-sm text-slate-500 mt-2">Infrastructure documentaire B2B. Dossier Flash pré-instruit en 47 secondes.</p>
          </div>
          <div>
            <div className="j-label mb-3">Espace Pro</div>
            <ul className="space-y-2 text-sm">
              <li><Link to="/preinstruction" className="hover:underline">Pré-instruction Dossier Flash</Link></li>
              <li><Link to="/vault" className="hover:underline">Coffre-Fort de Preuves</Link></li>
            </ul>
          </div>
          <div>
            <div className="j-label mb-3">Cadre légal</div>
            <ul className="space-y-2 text-sm text-slate-600">
              <li>Loi 71-1130 · Extraction factuelle</li>
              <li>Sources officielles (Etalab 2.0)</li>
              <li>Aucun conseil juridique personnalisé</li>
            </ul>
          </div>
        </div>
        <div className="border-t border-slate-200 py-4 text-center text-xs text-slate-500">
          © 2026 JurisFlash. Pré-instruction documentaire (Loi 71-1130 · Etalab 2.0).
        </div>
      </footer>
    );
  }

  return (
    <footer className="mt-24 border-t border-slate-200 bg-white dark:bg-transparent" data-testid="app-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 grid md:grid-cols-4 gap-8">
        <div>
          <div className="font-heading font-black text-lg">JurisFlash</div>
          <p className="text-sm text-slate-500 mt-2">Infrastructure documentaire B2C2B. Votre dossier structuré en 47 secondes.</p>
        </div>
        <div>
          <div className="j-label mb-3">Produit</div>
          <ul className="space-y-2 text-sm">
            <li><Link to="/" className="hover:underline">Flash Droit</Link></li>
            <li><Link to="/fiches" className="hover:underline">Fiches Réflexes (offline)</Link></li>
            <li><Link to="/preinstruction" className="hover:underline">Pré-instruction B2B</Link></li>
            <li><Link to="/vault" className="hover:underline">Coffre-Fort de Preuves</Link></li>
          </ul>
        </div>
        <div>
          <div className="j-label mb-3">Sécurité (Juris-Guardian)</div>
          <ul className="space-y-2 text-sm">
            <li>Anonymisation RGPD automatique</li>
            <li>Anti-hallucination Legifrance</li>
            <li>Coffre eIDAS-lite (SHA-256 + timestamp)</li>
            <li>Mode urgence hors-ligne (PWA)</li>
          </ul>
        </div>
        <div>
          <div className="j-label mb-3">Concours</div>
          <ul className="space-y-2 text-sm">
            <li><Link to="/concours" className="hover:underline">🏆 Concours Emergent</Link></li>
            <li>Loi du 31/12/1971</li>
            <li>Mentions légales</li>
            <li>Conformité RGPD</li>
          </ul>
        </div>
      </div>
      <div className="border-t border-slate-200 py-4 text-center text-xs text-slate-500">
        © 2026 JurisFlash. Pré-instruction documentaire (Loi 71-1130 · Etalab 2.0).
        · <Link to="/meteo" className="hover:underline text-slate-400" data-testid="footer-panic-link" title="Sortie rapide discrète">Météo Direct</Link>
      </div>
    </footer>
  );
}
